import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import HeaderButtons from './HeaderButtons';
import ProjectDropdown from './ProjectDropdown';
import DepartmentDropdown from './DepartmentDropdown';
import EstimateDropdown from './EstimateDropdown';
import HeaderSearchBox from './HeaderSearchBox';
import { useResponsive } from '../../hooks/useResponsive';
import { authenticatedApiCall } from '../../utils/apiClient';

// Hamburger menu icon component
const HamburgerIcon = ({ isOpen, onClick }) => (
  <button
    onClick={onClick}
    aria-label={isOpen ? 'Close menu' : 'Open menu'}
    aria-expanded={isOpen}
    style={{
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '5px',
      minWidth: '44px',
      minHeight: '44px',
    }}
  >
    <span style={{
      display: 'block',
      width: '24px',
      height: '2px',
      backgroundColor: 'white',
      borderRadius: '2px',
      transition: 'transform 0.3s ease, opacity 0.3s ease',
      transform: isOpen ? 'rotate(45deg) translateY(7px)' : 'none',
    }} />
    <span style={{
      display: 'block',
      width: '24px',
      height: '2px',
      backgroundColor: 'white',
      borderRadius: '2px',
      transition: 'opacity 0.3s ease',
      opacity: isOpen ? 0 : 1,
    }} />
    <span style={{
      display: 'block',
      width: '24px',
      height: '2px',
      backgroundColor: 'white',
      borderRadius: '2px',
      transition: 'transform 0.3s ease, opacity 0.3s ease',
      transform: isOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
    }} />
  </button>
);

// Mobile menu overlay
const MobileMenu = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 998,
          animation: 'fadeIn 0.2s ease',
        }}
      />
      {/* Menu panel */}
      <div
        style={{
          position: 'fixed',
          top: 'var(--header-height, 52px)',
          right: 0,
          width: '280px',
          maxWidth: '85vw',
          height: 'calc(100vh - var(--header-height, 52px))',
          backgroundColor: '#1a1a1a',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
          zIndex: 999,
          padding: '16px',
          overflowY: 'auto',
          animation: 'slideInRight 0.3s ease',
        }}
      >
        {children}
      </div>
      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

const Header = ({ user, permissions, showAdminPanel, setShowAdminPanel, handleLogout, onCreateProject, onHelpClick, onLearningClick, onContactClick, onFAQClick, selectedDepartment, onSelectDepartment, selectedEstimate = null, selectedEstimateId, onSelectEstimate, onSelectProject }) => {
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  
  // Extract projectId from URL path since Header is rendered outside Routes context
  // useParams() won't work here, so we parse from location.pathname
  const projectId = currentLocation.pathname.match(/^\/project\/(\d+)/)?.[1] || null;
  const { isMobile, isTablet } = useResponsive();

  // The full-width header (logo + title + user + three dropdowns + 260px search +
  // the button bar) needs roughly 1460px to lay out at its natural size. Below that
  // it used to push the button bar - including the hamburger - straight off screen,
  // which broke every page from 360px all the way up to 1280px laptops.
  //
  // Rather than jumping straight to the mobile header, drop the optional chrome in
  // priority order as space runs out. Each threshold is set at the width where the
  // element in question stops fitting, so nothing changes on a large desktop.
  // Thresholds come from measured overflow, not guesses: the baseline audit showed the
  // header fits at 1440 and 1920 but overflows from 1280 down, so 1440 must keep every
  // element. Below that, chrome is dropped in ascending order of importance.
  const hasRoomForTitle = useMediaQuery('(min-width:1500px)');
  const hasRoomForUserName = useMediaQuery('(min-width:1320px)');
  const hasRoomForWideSearch = useMediaQuery('(min-width:1100px)');

  // The header is taller on phones (the context switchers stack vertically), and
  // --header-height feeds six layout calculations: the app shell height, the project
  // shell height, the sidebar top/height and the mobile menu panel. Hard-coding a second
  // value would drift the moment the header's contents change, so measure the real
  // element and publish it. Every consumer then stays correct at any width.
  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--header-height', `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('orientationchange', publish);
    return () => { ro.disconnect(); window.removeEventListener('orientationchange', publish); };
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentProjectDetails, setCurrentProjectDetails] = useState(null);

  // Fetch current project details when on a project page
  useEffect(() => {
    const fetchCurrentProject = async () => {
      if (!projectId || !accounts[0]) {
        setCurrentProjectDetails(null);
        return;
      }
      
      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/projects/${projectId}`
        );
        if (response.data.success && response.data.project) {
          setCurrentProjectDetails(response.data.project);
        }
      } catch (error) {
        console.error('Error fetching current project:', error);
        setCurrentProjectDetails(null);
      }
    };

    fetchCurrentProject();
  }, [projectId, instance, accounts]);

  // Close mobile menu when navigating
  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Determine if we should show compact header
  const showCompact = isMobile || isTablet;

  // The three context switchers. On a phone they stack vertically at full width (see the
  // block below the top bar) instead of being squeezed side by side; on md+ they sit
  // inline exactly as before.
  const contextSwitchers = (fullWidth) => (
    <>
      <DepartmentDropdown
        selectedDepartment={selectedDepartment}
        onSelectDepartment={onSelectDepartment}
        compact={showCompact}
        shrink
        fullWidth={fullWidth}
      />
      {!fullWidth && !isMobile && hasRoomForWideSearch && (
        <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.3)', marginLeft: '0.5rem', marginRight: '0.5rem', flexShrink: 0 }} />
      )}
      <ProjectDropdown
        currentProjectDetails={currentProjectDetails}
        compact={showCompact}
        shrink
        fullWidth={fullWidth}
        onSelectDepartment={onSelectDepartment}
        onSelectProject={onSelectProject}
      />
      {!fullWidth && !isMobile && hasRoomForWideSearch && (
        <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.3)', marginLeft: '0.5rem', marginRight: '0.5rem', flexShrink: 0 }} />
      )}
      <EstimateDropdown
        compact={showCompact}
        shrink
        fullWidth={fullWidth}
        onSelectDepartment={onSelectDepartment}
        onSelectEstimate={onSelectEstimate}
        selectedEstimate={selectedEstimate || (selectedEstimateId ? { id: selectedEstimateId } : null)}
      />
    </>
  );

  return (
    <div 
      ref={headerRef}
      className="app-header no-print"
      style={{  
        background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(220, 53, 69, 0.8) 100%)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        borderBottom: '1px solid #1a1a1a',
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-header, 1000)',
        // Phones grow to fit the stacked context switchers; desktop keeps its exact
        // 56px bar. Height is never read back from the CSS variable we publish, to
        // avoid a measure/apply feedback loop.
        height: isMobile ? 'auto' : '56px',
      }}>
      {/* Logo positioned at extreme left, outside normal padding */}
      <div 
        data-tutorial="lgl-logo"
        style={{ 
          position: 'absolute',
          left: 0,
          top: 0,
          height: isMobile ? '52px' : '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          zIndex: 10
        }}
        onClick={() => handleNavigate('/')}
      >
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '6px',
          width: '46px',
          height: '46px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginLeft: '5px',
          overflow: 'hidden'
        }}>
          <img 
            src="/logo.png" 
            alt="LGL Logo" 
            style={{
              height: '72px',
              width: '72px',
              objectFit: 'cover'
            }}
          />
        </div>
      </div>

      {/* Top row with buttons - adjusted for absolute logo */}
      <div style={{ 
        // The logo is absolutely positioned, so the row is padded clear of it.
        // Phones reclaim the spare gap either side of the 46px logo.
        paddingLeft: isMobile ? '54px' : '65px',
        paddingRight: showCompact ? '0.75rem' : '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
        gap: '0.5rem',
        minWidth: 0,
      }}>
        {/* Left side - User info.
            flex 1 1 auto + minWidth 0 is what stops this cluster from forcing the row
            wider than the viewport; without it the right-hand controls (search and,
            critically, the hamburger) were pushed off screen and the menu became
            unreachable on phones. */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: showCompact ? '0.5rem' : '0.75rem',
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
        }}>
          {/* Title - first thing to go when space runs short (the logo still brands it) */}
          {!isMobile && hasRoomForTitle && (
            <>
              <div style={{ flexShrink: 0 }}>
                <h1 style={{ 
                  margin: 0, 
                  color: 'white',
                  fontSize: showCompact ? '1.1rem' : '1.3rem',
                  fontWeight: '700',
                  letterSpacing: '-0.3px',
                  lineHeight: '1.2'
                }}>
                  LGL Portal
                </h1>
              </div>
            </>
          )}
          
          {/* Divider and username - dropped next; the avatar menu still shows who you are */}
          {!isMobile && hasRoomForUserName && (
            <>
              <div style={{
                width: '1px',
                height: '24px',
                backgroundColor: 'rgba(255,255,255,0.3)',
                marginLeft: '0.5rem',
                marginRight: '0.5rem',
                flexShrink: 0,
              }}></div>
              <span style={{ 
                color: 'rgba(255,255,255,0.95)',
                fontSize: showCompact ? '0.85rem' : '0.95rem',
                fontWeight: '500',
                maxWidth: isTablet ? '120px' : '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 1,
                minWidth: 0,
              }}>
                {user?.display_name}
              </span>
            </>
          )}
          
          {/* Divider - hide on mobile */}
          {!isMobile && hasRoomForUserName && (
            <div style={{
              width: '1px',
              height: '24px',
              backgroundColor: 'rgba(255,255,255,0.3)',
              marginLeft: '0.5rem',
              marginRight: '0.5rem',
              flexShrink: 0,
            }}></div>
          )}
          
          {/* Context switchers inline on md+; on phones they live in the stack below. */}
          {!isMobile && contextSwitchers(false)}

          {/* Desktop: search sits with the left controls so it's always visible.
              Between 992px and 1200px the 260px field no longer fits alongside the
              dropdowns, so fall back to the same compact field used on phones. */}
          {!showCompact && (
            <HeaderSearchBox
              compact={!hasRoomForWideSearch}
              projectId={projectId}
              selectedEstimateId={selectedEstimateId}
              onSelectDepartment={onSelectDepartment}
            />
          )}
        </div>
        
        {/* Right side - Buttons or hamburger.
            flexShrink 0 keeps these controls on screen no matter how wide the left
            cluster wants to be. The hamburger is the only way into the menu on a
            phone, so it must never be the thing that overflows. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, flexShrink: 0 }}>
          {showCompact ? (
            // Mobile/Tablet: compact search field + hamburger
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
              <HeaderSearchBox
                compact
                projectId={projectId}
                selectedEstimateId={selectedEstimateId}
                onSelectDepartment={onSelectDepartment}
              />
              <HamburgerIcon 
                isOpen={mobileMenuOpen} 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              />
            </div>
          ) : (
            // Desktop: Show full button bar
            <HeaderButtons
              user={user}
              permissions={permissions}
              location={currentLocation}
              showAdminPanel={showAdminPanel}
              setShowAdminPanel={setShowAdminPanel}
              handleLogout={handleLogout}
              onCreateProject={onCreateProject}
              onHelpClick={onHelpClick}
              onLearningClick={onLearningClick}
              onContactClick={onContactClick}
              onFAQClick={onFAQClick}
            />
          )}
        </div>
      </div>

      {/* Phone context bar: the three switchers stacked full width, each showing its
          complete label instead of being crushed to a few characters side by side. */}
      {isMobile && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          padding: '0 0.75rem 8px',
        }}>
          {contextSwitchers(true)}
        </div>
      )}

      {/* Mobile Menu */}
      {showCompact && (
        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
          {/* User info */}
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '4px' }}>
              Signed in as
            </div>
            <div style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
              {user?.display_name}
            </div>
          </div>

          {/* Navigation buttons - stacked vertically */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <MobileMenuButton 
              onClick={() => { handleNavigate('/'); setShowAdminPanel(false); }}
              label="🏠 Home"
              isActive={currentLocation.pathname === '/' && !showAdminPanel}
            />
            {permissions?.isPM && currentLocation.pathname === '/projects' && onCreateProject && (
              <MobileMenuButton 
                onClick={() => { onCreateProject(); setMobileMenuOpen(false); }}
                label="➕ New Project"
              />
            )}
            
            {permissions?.canAccessAdminPanel && (
              <MobileMenuButton 
                onClick={() => { 
                  setShowAdminPanel(!showAdminPanel);
                  setMobileMenuOpen(false); 
                }}
                label={`⚙️ ${permissions?.isAdmin ? 'Admin Panel' : 'Project Management'}`}
                isActive={showAdminPanel}
              />
            )}
            
            <div style={{ 
              height: '1px', 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              margin: '8px 0' 
            }} />
            
            {/* Help Section */}
            <MobileMenuButton
              onClick={() => { onHelpClick && onHelpClick(); setMobileMenuOpen(false); }}
              label="📖 Help"
            />
            <MobileMenuButton
              onClick={() => { onFAQClick && onFAQClick(); setMobileMenuOpen(false); }}
              label="❓ FAQs"
            />
            <MobileMenuButton
              onClick={() => { onLearningClick && onLearningClick(); setMobileMenuOpen(false); }}
              label="🎓 Learning Center"
            />
            <MobileMenuButton
              onClick={() => { onContactClick && onContactClick(); setMobileMenuOpen(false); }}
              label="🎧 Contact Us & Support"
            />
            
            <MobileMenuButton 
              onClick={() => window.print()}
              label="🖨️ Print Page"
            />
            
            <div style={{ 
              height: '1px', 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              margin: '8px 0' 
            }} />
            
            <MobileMenuButton 
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              label="🚪 Sign Out"
              variant="danger"
            />
          </div>
        </MobileMenu>
      )}
    </div>
  );
};

// Mobile menu button component
const MobileMenuButton = ({ onClick, label, isActive, variant }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: '14px 16px',
      border: 'none',
      borderRadius: '8px',
      backgroundColor: isActive 
        ? 'rgba(220, 53, 69, 0.3)' 
        : variant === 'danger' 
          ? 'rgba(220, 53, 69, 0.1)' 
          : 'rgba(255, 255, 255, 0.05)',
      color: variant === 'danger' ? '#ff6b6b' : 'white',
      fontSize: '15px',
      fontWeight: isActive ? '600' : '500',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.2s ease',
      minHeight: '48px',
    }}
  >
    {label}
  </button>
);

export default Header;
